import { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useNavigationPermissions } from "@/hooks/useNavigationPermissions";

type AdminRoleGuardProps = {
  children: React.ReactNode;
};

export function AdminRoleGuard({ children }: AdminRoleGuardProps) {
  console.log("AdminRoleGuard: render start");
  const { user, loading, userRole, isAdmin, ready } = useAuth();
  const { canAccessAdminRoutes } = useNavigationPermissions();
  const location = useLocation();
  
  // Add timeout to prevent infinite spinner on auth problems
  const [showSpinner, setShowSpinner] = useState(true);
  // New: Track if we're checking email verification
  const [checkingEmailVerification, setCheckingEmailVerification] = useState(true);
  
  // New: Effect to check email verification status
  useEffect(() => {
    if (!user) {
      setCheckingEmailVerification(false);
      return;
    }
    // Log the email_confirmed_at value and userRole for diagnosis
    console.log("AdminRoleGuard: email_confirmed_at check", {
      email_confirmed_at: user.email_confirmed_at,
      userRole: user.user_metadata?.role,
      user
    });
    // Skip email verification check for bizadmin users
    if (user.user_metadata?.role === 'bizadmin') {
      setCheckingEmailVerification(false);
      return;
    }
    // If user is loaded, check email_confirmed_at
    if (user.email_confirmed_at) {
      setCheckingEmailVerification(false);
    } else {
      setCheckingEmailVerification(true);
    }
  }, [user]);

  useEffect(() => {
    // Only stop showing spinner when auth is truly ready AND we have role data
    if (!loading && ready && userRole) {
      setShowSpinner(false);
    }
    
    // Add a maximum timeout to prevent infinite loading
    const timer = setTimeout(() => {
      if (loading || !ready) {
        console.warn("AdminRoleGuard: Auth loading is taking too long, forcing proceed");
      }
      setShowSpinner(false);
    }, 5000); // 5 second timeout
    
    return () => clearTimeout(timer);
  }, [loading, ready, userRole]);

  console.log("AdminRoleGuard Debug:", { 
    user: !!user, 
    loading,
    ready,
    userRole,
    isAdmin,
    canAccessAdminRoutes,
    path: location.pathname,
    showSpinner,
    userMetadata: user?.user_metadata
  });

  // skip in dev
  if (import.meta.env.MODE === "development") {
    console.log("Development mode: bypassing role guard");
    return <>{children}</>;
  }

  // Fast-path: never show spinner for bizadmin users
  if (user && user.user_metadata?.role === 'bizadmin') {
    return <>{children}</>;
  }

  // Show spinner if we're checking email verification or not ready or loading
  if (checkingEmailVerification || !ready || loading || showSpinner) {
    console.log("AdminRoleGuard: showing spinner", { checkingEmailVerification, ready, loading, showSpinner });
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  // not signed in
  if (!user) {
    console.log("AdminRoleGuard: Not signed in, redirecting to app domain auth");
    
    // Check if we're on marketing domain and need to redirect to app domain
    if (typeof window !== 'undefined' && !window.location.hostname.includes('app.')) {
      // On marketing domain - redirect to app domain for auth
      const appUrl = import.meta.env.DEV 
        ? (import.meta.env.VITE_SITE_URL || 'http://localhost:5173')
        : (import.meta.env.VITE_APP_URL || 'https://app.hivetivity.com');
      window.location.href = appUrl + '/auth';
      return null;
    } else {
      // Already on app domain, use normal navigation
      return <Navigate to="/auth" state={{ from: location }} replace />;
    }
  }

  // New: If user is not verified, redirect to email verification page
  if (!user.email_confirmed_at) {
    return <Navigate to="/email-verification" state={{ email: user.email }} replace />;
  }

  // check if user can access admin routes
  if (canAccessAdminRoutes) {
    // If user is a superadmin, redirect them to the superadmin dashboard
    // unless they're explicitly trying to access an admin route
    const isSuperAdmin = userRole === 'superadmin';
    if (isSuperAdmin && !location.pathname.startsWith('/admin/')) {
      console.log("AdminRoleGuard: Superadmin detected, redirecting to superadmin dashboard");
      return <Navigate to="/superadmin" replace />;
    }
    
    console.log("AdminRoleGuard: access granted", { userRole, isAdmin, userMetadata: user.user_metadata });
    return <>{children}</>;
  }

  console.log("AdminRoleGuard: access denied", { 
    userRole, 
    isAdmin, 
    canAccessAdminRoutes,
    userMetadata: user.user_metadata 
  });
  return <Navigate to="/unauthorized" state={{ from: location }} replace />;
}

export default AdminRoleGuard;
